import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Minus, Plus, X, ArrowRight } from 'lucide-react';
import { API, normalizeRenderableImageUrl } from '../lib/api';
import { useWholesaleAuth } from '../context/WholesaleAuthContext';
import { useWholesaleCart } from '../context/WholesaleCartContext';

const WholesaleCartPage = () => {
    const { getAuthHeaders } = useWholesaleAuth();
    const { items, updateQuantity, removeItem, clearCart, total } = useWholesaleCart();
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const submitOrder = async () => {
        if (!items.length) return;
        setIsSubmitting(true);
        try {
            const response = await axios.post(`${API}/wholesale/orders`, {
                items: items.map(item => ({ product_id: item.product.id, quantity: item.quantity })),
                notes: notes || null,
            }, { headers: getAuthHeaders() });
            alert(`Wholesale order submitted: ${response.data.order_number}`);
            clearCart();
            setNotes('');
        } catch (error) {
            alert(error.response?.data?.detail || 'Failed to submit wholesale order');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <section className="pt-24 pb-12 border-b border-black/10">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <p className="text-maple-red font-mono text-sm tracking-widest">WHOLESALE ORDER</p>
                    <h1 className="font-anton text-5xl text-maple-dark mt-4">CART</h1>
                </div>
            </section>
            <section className="py-12">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    {items.length === 0 ? (
                        <div className="text-center py-20 border border-black/10">
                            <p className="text-maple-dark/40 font-mono text-lg mb-6">Your wholesale cart is empty</p>
                            <Link to="/wholesale/products" className="btn-primary inline-block"><span>Browse Wholesale Products</span></Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                            <div className="lg:col-span-2 space-y-6">
                                {items.map(item => {
                                    const image = normalizeRenderableImageUrl(item.product.main_image) || normalizeRenderableImageUrl(item.product.images?.[0]);
                                    return (
                                        <div key={item.product.id} className="flex gap-6 bg-maple-secondary border border-black/10 p-4 md:p-6">
                                            <div className="w-24 h-24 md:w-32 md:h-32 flex-shrink-0 bg-white overflow-hidden">
                                                {image ? <img src={image} alt={item.product.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-black/20 text-xs">No Image</div>}
                                            </div>
                                            <div className="flex-1 flex flex-col justify-between">
                                                <div>
                                                    <span className="text-maple-dark/40 text-xs font-mono uppercase">{item.product.wholesale_category || item.product.category}</span>
                                                    <h3 className="font-anton text-lg md:text-xl text-maple-dark mt-1">{item.product.name}</h3>
                                                    <p className="font-mono text-maple-dark/60 mt-1">${Number(item.product.wholesale_price || 0).toFixed(2)} per batch</p>
                                                    <p className="text-maple-dark/50 text-sm mt-1">Batch: {item.product.wholesale_batch_size || '—'} {item.product.wholesale_unit_label || 'units'}</p>
                                                </div>
                                                <div className="flex items-center justify-between mt-4">
                                                    <div className="flex items-center gap-3">
                                                        <button onClick={() => updateQuantity(item.product.id, item.quantity - 1)} className="w-8 h-8 bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors text-maple-dark"><Minus className="w-4 h-4" /></button>
                                                        <span className="font-mono w-8 text-center text-maple-dark">{item.quantity}</span>
                                                        <button onClick={() => updateQuantity(item.product.id, item.quantity + 1)} className="w-8 h-8 bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors text-maple-dark"><Plus className="w-4 h-4" /></button>
                                                    </div>
                                                    <button onClick={() => removeItem(item.product.id)} className="p-2 text-maple-dark/40 hover:text-maple-red transition-colors"><X className="w-5 h-5" /></button>
                                                </div>
                                            </div>
                                            <div className="hidden md:flex flex-col justify-center">
                                                <span className="font-mono text-lg text-maple-dark">${(Number(item.product.wholesale_price || 0) * item.quantity).toFixed(2)}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                                <button onClick={clearCart} className="text-maple-dark/40 hover:text-maple-red text-sm transition-colors">Clear Cart</button>
                            </div>
                            <div className="lg:col-span-1">
                                <div className="bg-maple-secondary border border-black/10 p-6 md:p-8 sticky top-28">
                                    <h2 className="font-anton text-2xl text-maple-dark mb-6">ORDER SUMMARY</h2>
                                    <div className="flex justify-between text-maple-dark text-lg mt-6">
                                        <span className="font-anton">TOTAL</span>
                                        <span className="font-mono">${total.toFixed(2)}</span>
                                    </div>
                                    <div className="mt-6">
                                        <label className="text-maple-dark/60 text-xs font-mono uppercase tracking-wider block mb-2">Notes</label>
                                        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} className="w-full bg-white border border-black/10 px-3 py-2 text-sm text-maple-dark resize-none" placeholder="Delivery notes, reorder preferences, etc." />
                                    </div>
                                    <button onClick={submitOrder} disabled={isSubmitting} className="btn-primary w-full mt-8 flex items-center justify-center gap-2 disabled:opacity-50">
                                        <span>{isSubmitting ? 'Submitting...' : 'Submit Wholesale Order'}</span>
                                        {!isSubmitting && <ArrowRight className="w-4 h-4" style={{ transform: 'skewX(6deg)' }} />}
                                    </button>
                                    <Link to="/wholesale/products" className="block text-center text-maple-dark/40 hover:text-maple-dark text-sm mt-4 transition-colors">Continue Ordering</Link>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default WholesaleCartPage;
